import type { TenantUserRead } from '@pedigreesmart/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { useSession } from '../session/SessionProvider.js'
import '../styles/forms.css'
import './settings.css'

/** I ruoli assegnabili, con il nome che hanno in segreteria. */
const RUOLI: readonly { valore: string; etichetta: string; spiegazione: string }[] = [
  {
    valore: 'ROLE_TENANT_OWNER',
    etichetta: 'Titolare',
    spiegazione: 'Può tutto, compreso gestire gli utenti e le impostazioni.',
  },
  {
    valore: 'ROLE_TENANT_ADMIN',
    etichetta: 'Amministratore',
    spiegazione: 'Gestisce registro, anagrafica e denunce.',
  },
  {
    valore: 'ROLE_TENANT_STAFF',
    etichetta: 'Segreteria',
    spiegazione: 'Lavora su registro e denunce, senza toccare le impostazioni.',
  },
  {
    valore: 'ROLE_TENANT_BREEDER',
    etichetta: 'Allevatore',
    spiegazione: 'Vede e gestisce solo i propri soggetti.',
  },
]

/**
 * Chi può entrare per conto dell'associazione.
 *
 * **Da qui non si creano profili.** Un profilo nuovo nasce da un invito o dal
 * comando `pedigreesmart:user:create`: creare credenziali vuol dire scegliere
 * una password e consegnarla a qualcuno, e non è un'operazione che si fa con
 * un pulsante in fondo a un elenco. Da qui si cambia il ruolo e si toglie
 * l'accesso, che sono le due cose urgenti — soprattutto la seconda.
 *
 * **Togliere l'accesso non cancella niente.** L'utente disattivato smette di
 * entrare ma resta nell'elenco, e resta anche il suo nome sulle operazioni che
 * ha fatto: un registro in cui le firme spariscono con chi le ha messe non è
 * un registro.
 */
export function TenantUserList() {
  const { resources, context } = useSession()

  const utenti = useQuery({
    queryKey: ['tenant-users'],
    queryFn: ({ signal }) => resources.tenantUsers.list(signal),
  })

  return (
    <section aria-labelledby="titolo-utenti">
      <div className="elenco__testa">
        <h1 id="titolo-utenti">Utenti</h1>
        {utenti.data && (
          <p className="elenco__conteggio" aria-live="polite">
            {utenti.data.totalItems === 1 ? '1 profilo' : `${utenti.data.totalItems} profili`}
          </p>
        )}
      </div>

      <p className="messaggio">
        I profili nuovi si creano con un invito, non da questa schermata. Qui si cambia il ruolo e
        si toglie l'accesso.
      </p>

      {utenti.isError && (
        <p role="alert" className="messaggio messaggio--errore">
          Non è stato possibile leggere l'elenco dei profili.
        </p>
      )}

      {utenti.isPending && <p className="messaggio">Caricamento…</p>}

      {utenti.data && (
        <div className="tabella-scorrevole">
          <table>
            <thead>
              <tr>
                <th scope="col">Indirizzo di accesso</th>
                <th scope="col">Persona</th>
                <th scope="col">Ruolo</th>
                <th scope="col">Accesso</th>
              </tr>
            </thead>
            <tbody>
              {utenti.data.member.map((utente) => (
                <Riga
                  key={utente.id}
                  utente={utente}
                  // Il proprio profilo non si tocca da qui: togliersi l'accesso
                  // da soli è un modo silenzioso di chiudersi fuori, e in
                  // un'associazione con un solo titolare non lo riapre nessuno.
                  seStesso={utente.login_email === context?.user.email}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function Riga({ utente, seStesso }: { utente: TenantUserRead; seStesso: boolean }) {
  const { resources } = useSession()
  const cache = useQueryClient()
  const [errore, setErrore] = useState<string | null>(null)

  const salva = useMutation({
    mutationFn: (modifica: { role?: string; active?: boolean }) =>
      resources.tenantUsers.update(utente.id, modifica),
    onMutate: () => {
      setErrore(null)
    },
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['tenant-users'] })
    },
    onError: () => {
      setErrore('Non è stato possibile salvare la modifica.')
    },
  })

  const ruolo = RUOLI.find((r) => r.valore === utente.role)

  return (
    <tr>
      <td>
        <span className="riga__nome">{utente.login_email}</span>
        {seStesso && <span className="riga__secondario">siete voi</span>}
        {errore && (
          <span className="modulo__errore" role="alert">
            {errore}
          </span>
        )}
      </td>
      <td>{utente.person?.display_name ?? '—'}</td>
      <td>
        <label>
          <span className="solo-lettore">Ruolo di {utente.login_email}</span>
          <select
            value={utente.role}
            disabled={seStesso || salva.isPending}
            onChange={(evento) => {
              salva.mutate({ role: evento.target.value })
            }}
          >
            {/* Un ruolo sconosciuto resta selezionabile invece di sparire: il
                catalogo dei ruoli può crescere, e una tendina che non contiene
                il valore corrente ne mostrerebbe un altro come se fosse quello
                vero. */}
            {!ruolo && <option value={utente.role}>{utente.role}</option>}
            {RUOLI.map((r) => (
              <option key={r.valore} value={r.valore}>
                {r.etichetta}
              </option>
            ))}
          </select>
        </label>
        {ruolo && <span className="riga__secondario">{ruolo.spiegazione}</span>}
      </td>
      <td>
        <label className="persone__interruttore">
          <input
            type="checkbox"
            checked={utente.active}
            disabled={seStesso || salva.isPending}
            onChange={(evento) => {
              salva.mutate({ active: evento.target.checked })
            }}
          />
          {utente.active ? 'Attivo' : 'Sospeso'}
        </label>
      </td>
    </tr>
  )
}
